<template>
    <main class="dm-page-container dm-flex-column dm-padding-0">
        <menu-header />

        <nav id="menuNutritionPageNav" v-if="publicMenu.IsPublicMenuPublished" class="app-filter-container dm-flex-row app-phone-flex-column">
            <div class="filters">
                <school-meal-filters />
            </div>
             <menu-multiselect
                v-model="selectedTags"
                :items="allTags"
                label="Tags"
                class="app-phone-full-width"
            />

            <menu-multiselect
                v-model="selectedAllergens"
                :items="allAllergens"
                label="Allergens"
                class="app-phone-full-width"
            />

            <menu-multiselect
                v-model="selectedCategories"
                :items="allCategories"
                label="Categories"
                class="app-phone-full-width"
            />
            
            <div class="app-phone-hide spacing"></div>
            <div class="app-phone-full-width search-container">
                <div class="search-input-wrapper ">
                    <i class="app-icon-magnifying-glass search-icon" aria-hidden="true"></i>
                    <div id="search-help" class="sr-only">Type to search through menu items by name</div>
                    <ejs-textbox 
                        id="searchBox" 
                        v-model="searchText" 
                        placeholder="Search" 
                        class="search-textbox"
                        aria-label="Search menu items"
                        aria-describedby="search-help"
                    > </ejs-textbox>
                    <button 
                        v-if="searchText" 
                        @click="clearSearch" 
                        class="search-clear" 
                        type="button"
                        aria-label="Clear search"
                    >
                        <i class="fa-solid fa-xmark" aria-hidden="true"></i>
                    </button>
                </div>
            </div>           
        </nav>

        <div v-if="publicMenu.IsPublicMenuPublished && siteID" class="menu-item-table-container">
            <menu-item-table
                :items="pagedFilteredItems"
                :sort-column="sortColumn"
                :sort-direction="sortDirection"
                @sort="sortBy"
            />
            
            <pagination 
                v-if="filteredItems.length > 0 && filteredItems.length > pageSize"
                :total-items="filteredItems.length"
                :page-size="pageSize"
                :current-page="currentPage"
                @page-change="handlePageChange"
                @page-size-change="handlePageSizeChange"
            />
        </div>
    </main>
</template>

<script>
import menuButton from '../../components/menuButton.vue';
import menuHeader from '../../components/menuHeader.vue';
import menuMultiSelect from '../../components/menuMultiSelect.vue';
import pagination from '../../components/pagination.vue';
import schoolMealFilters from '../../components/schoolMealFilters.vue';
import adaMixin from '../../js/mixins/adaMixin.js';
import menuItemTable from './menuItemTable.vue';

export default {
    name: 'nutrition-allergen-page',
    mixins: [adaMixin],
    components: { 
        'school-meal-filters': schoolMealFilters, 
        'menu-header': menuHeader,
        'menu-button': menuButton,
        'menu-item-table': menuItemTable,
        'menu-multiselect': menuMultiSelect,
        'pagination': pagination,
    },
    data() {
        return {
            nutritionTableColumns: [],
            nutritionTableSortBy: {
                columns: [{ field: 'DisplayName', direction: 'Ascending' }],
            },
            nutritionTableActions: [],
            selectedRow: null,
            searchText: '',
            debouncedSearchText: '',
            sortColumn: null,
            sortDirection: 'asc',
            searchDebounceTimer: null,
            selectedTags: [],
            selectedAllergens: [],
            selectedCategories: [],
            currentPage: 1,
            pageSize: 100,
        };
    },
    computed: {
        siteID() {
            return this.$store.state.PublicMenuModule.siteID
        },
        mealTimeId() {
            return this.$store.state.PublicMenuModule.mealTimeId
        },
        publicMenu() {
            return this.$store.state.PublicMenuModule.publicMenu;
        },
        sites() {
            return this.$store.state.PublicMenuModule.sites;
        },
        mealTimes() {
            return this.$store.state.PublicMenuModule.mealTimes;
        },
        nutritionDetails() {
            return this.$store.state.PublicMenuModule.nutritionDetails;
        },
        allTags() {
            return this.$store.state.PublicMenuModule.nutritionDetails.Tags || [];
        },
        allAllergens() {
            const standardAllergens = this.$store.state.PublicMenuModule.allergens.map(a => ({
                value: a.AllergenId,
                label: a.AllergenName,
            }));
            
            const customAllergens = [...new Set((this.nutritionDetails.MenuItems || []).flatMap(item => item.CustomAllergens || []))]           
                .map(ca => ({ value: `custom_${ca}`, label: ca }))

            return [...standardAllergens, ...customAllergens];
        },
        allCategories() {
            return this.$store.state.PublicMenuModule.categories.map(c => ({
                value: c.CategoryId,
                label: c.DisplayName,
            }));
        },
        filteredItems() {
            let items = this.nutritionDetails.MenuItems || [];
            
            const matchesSearch = (item) => {
                if (!this.debouncedSearchText) return true;
                return item.DisplayName.toLowerCase().includes(this.debouncedSearchText.toLowerCase());
            };
            
            const matchesTags = (item) => {
                if (this.selectedTags.length === 0) return true;
                if (!item.Tags || item.Tags.length === 0) return false;
                return this.selectedTags.some(selectedTag => item.Tags.includes(selectedTag));
            };
            
            const matchesAllergens = (item) => {
                if (this.selectedAllergens.length === 0) return true;
                return this.selectedAllergens.some(selectedAllergen => {
                    if (typeof selectedAllergen === 'string' && selectedAllergen.startsWith('custom_')) {
                        const customAllergenName = selectedAllergen.substring(7);
                        return item.CustomAllergens && item.CustomAllergens.includes(customAllergenName);
                    } else {
                        return item.Allergens && item.Allergens.includes(selectedAllergen);
                    }
                });
            };
            
            const matchesCategories = (item) => {
                if (this.selectedCategories.length === 0) return true;
                return this.selectedCategories.includes(item.CategoryId);
            };
            
            const matchesAllFilters = (item) => {
                return matchesSearch(item) && matchesTags(item) && matchesAllergens(item) && matchesCategories(item);
            };
            
            items = items.reduce((filtered, item) => {
                const parentMatches = matchesAllFilters(item);
                
                const matchingChildren = item.ChildItems 
                    ? item.ChildItems.filter(child => matchesAllFilters(child))
                    : [];
                
                if (parentMatches || matchingChildren.length > 0) {
                    const filteredItem = {
                        ...item,
                        ChildItems: parentMatches ? item.ChildItems : matchingChildren,
                        hasFilteredChildren: !parentMatches && matchingChildren.length > 0
                    };
                    filtered.push(filteredItem);
                }
                
                return filtered;
            }, []);

            if (this.sortColumn) {
                items = [...items].sort((a, b) => {
                    let aValue, bValue;
                    
                    if (this.sortColumn === 'DisplayName') {
                        aValue = a.DisplayName || '';
                        bValue = b.DisplayName || '';
                    } else if (this.sortColumn === 'ServingDisplay') {
                        aValue = a.ServingDisplay || '';
                        bValue = b.ServingDisplay || '';
                    } else {
                        aValue = a.NutritionAmounts && a.NutritionAmounts[this.sortColumn] 
                            ? parseFloat(a.NutritionAmounts[this.sortColumn].Amount) || 0 
                            : 0;
                        bValue = b.NutritionAmounts && b.NutritionAmounts[this.sortColumn] 
                            ? parseFloat(b.NutritionAmounts[this.sortColumn].Amount) || 0 
                            : 0;
                    }
                    
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        const comparison = aValue.localeCompare(bValue);
                        return this.sortDirection === 'asc' ? comparison : -comparison;
                    }
                    
                    if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
                    if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
                    return 0;
                });
            }
            
            return items;
        },
        pagedFilteredItems() {
            const startIndex = (this.currentPage - 1) * this.pageSize;
            const endIndex = startIndex + this.pageSize;
            return this.filteredItems.slice(startIndex, endIndex);
        },
    },
    watch: {
        nutritionDetails(newVal) {
            if(newVal.MenuItems && newVal.MenuItems.length > 0) {
                this.setAndClearAnnouncement(`Menu data loaded. ${newVal.MenuItems.length} menu items available.`);
            }
        },
        searchText(newVal) {
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }
            
            this.searchDebounceTimer = setTimeout(() => {
                this.debouncedSearchText = newVal;
            }, 500);
        },
        debouncedSearchText() {
            this.currentPage = 1;
            this.filteringTable(this.filteredItems);
            if (this.debouncedSearchText) {
                this.setAndClearAnnouncement(`Search completed. ${this.filteredItems.length} menu items found for "${this.debouncedSearchText}"`);
            } else {
                this.setAndClearAnnouncement(`Search cleared. Showing all ${this.filteredItems.length} menu items`);
            }
        },
        selectedTags() {
            this.currentPage = 1;
            this.filteringTable(this.filteredItems);
            if (this.selectedTags.length > 0) {
                const tagNames = this.selectedTags.join(', ');
                this.setAndClearAnnouncement(`Filtered by tags: ${tagNames}. ${this.filteredItems.length} results found.`);
            } else {
                this.setAndClearAnnouncement(`Tag filters cleared. Showing all ${this.filteredItems.length} menu items.`);
            }
        },
        selectedAllergens() {
            this.currentPage = 1;
            this.filteringTable(this.filteredItems);
            if (this.selectedAllergens.length > 0) {
                const allergenNames = this.selectedAllergens.map(id => {
                    const allergen = this.allAllergens.find(a => a.value === id);
                    return allergen ? allergen.label : id;
                }).join(', ');
                this.setAndClearAnnouncement(`Filtered by allergens: ${allergenNames}. ${this.filteredItems.length} results found.`);
            } else {
                this.setAndClearAnnouncement(`Allergen filters cleared. Showing all ${this.filteredItems.length} menu items.`);
            }
        },
        selectedCategories() {
            this.currentPage = 1;
            this.filteringTable(this.filteredItems);
            if (this.selectedCategories.length > 0) {
                const categoryNames = this.selectedCategories.map(id => {
                    const category = this.allCategories.find(c => c.value === id);
                    return category ? category.label : id;
                }).join(', ');
                this.setAndClearAnnouncement(`Filtered by categories: ${categoryNames}. ${this.filteredItems.length} results found.`);
            } else {
                this.setAndClearAnnouncement(`Category filters cleared. Showing all ${this.filteredItems.length} menu items.`);
            }
        },
    },
    methods: {
        searchNutrition() {
            return this.$store.dispatch('PublicMenuModule/getNutritionDetails');
        },
        printPage() {
            window.print();
        },
        sortBy(column) {
            const columnName = this.getColumnName(column);
            
            if (this.sortColumn === column) {
                if (this.sortDirection === 'asc') {
                    this.sortDirection = 'desc';
                    this.setAndClearAnnouncement(`Sorted by ${columnName} descending`);
                } else {
                    this.sortColumn = null;
                    this.sortDirection = 'asc';
                    this.setAndClearAnnouncement('Sorting removed');
                }
            } else {
                this.sortColumn = column;
                this.sortDirection = 'asc';
                this.setAndClearAnnouncement(`Sorted by ${columnName} ascending`);
            }
        },
        getColumnName(column) {
            if (column === 'DisplayName') return 'Menu Item';
            if (column === 'ServingDisplay') return 'Serving';
            const nutritionCol = this.$store.state.PublicMenuModule.nutrition.find(n => n.NutritionComponentId === column);
            return nutritionCol ? nutritionCol.ShortName : column;
        },
        clearSearch() {
            this.searchText = '';
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }
            this.debouncedSearchText = '';
        },
        handlePageChange(page) {
            this.currentPage = page;
        },
        handlePageSizeChange(size) {
            this.pageSize = size;
            this.currentPage = 1;
        },
    },
    beforeDestroy() {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
    }
};
</script>
<style scoped>
.app-filter-container {
    gap: 16px;
    margin-bottom: 32px;
}

.spacing {
    flex: 1;
}

.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 12px;
    color: rgba(138, 140, 144, 1);
    font-size: 14px;
    z-index: 1;
    pointer-events: none;
}

.search-textbox {
    font-size: 16px !important;
    padding-left: 36px !important;
    text-align: left !important;
}



.search-textbox:focus,
.search-textbox.e-input-focus,
.search-input-wrapper .e-input-group.e-input-focus,
.search-input-wrapper .e-input-group:focus,
.search-input-wrapper .e-control.e-textbox.e-lib.e-input:focus {
    outline: none !important;
    box-shadow: 0 0 0 2px #C2E66E !important;
}

.search-clear {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    color: rgba(138, 140, 144, 1);
    font-size: 14px;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
}

.search-clear:hover {
    background-color: rgba(225, 225, 226, 0.3);
    color: rgba(51, 51, 51, 1);
}

.menu-item-table-container {
    overflow-x: auto;
    border: 1px solid rgba(225, 225, 226, 1);
    border-radius: 16px;
    position: relative;
}

.menu-item-table-container::-webkit-scrollbar {
    height: 6px;
}

.menu-item-table-container::-webkit-scrollbar-track {
    background: transparent;
    margin: 0 16px;
}

.menu-item-table-container::-webkit-scrollbar-thumb {
    background: rgba(138, 140, 144, 0.5);
    border-radius: 3px;
    border: 0 solid transparent;
}

.menu-item-table-container::-webkit-scrollbar-thumb:hover {
    background: rgba(138, 140, 144, 0.8);
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

</style>
